// Angular imports
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

// cc-common imports
import { IconConstants, Utils } from 'cc-common';

// devextreme imports
import notify from 'devextreme/ui/notify';
import { formatNumber } from 'devextreme/localization';

// constant imports
import { Constants } from '../../../../_constants/constants';
import { StringFormatConstants } from '../../../../_constants/string-format-constants';
import { MessageInterval } from '../../../../_constants/messages-intervals';

// service imports
import { APPaymentService } from '../../_services/accounts-payable/ap-payment.service';
import { ServerErrorService } from '../../../../_services/shared-services/server-error.service';
import { HelperService } from '../../../../_services/shared-services/helper.service';
import { APCreditApplicationsService } from '../../_services/accounts-payable/ap-credit-applications.service';
import { PrecisionService } from '../../../../_services/shared-services/precision.service';
import { ContextManagerService } from '../../../../_services/data-services/context-manager.service';
import { UserService } from '../../../../_services/data-services/user.service';

// model imports
import { PropertyCommunityInfoResultDto } from '../../../../_models/property-community-info-result-dto';
import { GLSubcategorySelectorResultDto } from '../../_models/common/gl-account/glsubcategory-selector-result-dto';
import { TransactionLogResultDto } from '../../_models/common/transaction-log-result-dto';
import { TransactionLogParameterDto } from '../../_models/common/transaction-log-parameter-dto';
import { APPaymentParameterDto } from '../../_models/accounts-payable/ap-payment/ap-payment-parameter-dto';
import { APPaymentResultDto } from '../../_models/accounts-payable/ap-payment/ap-payment-result-dto';
import { APServiceChargeEditorParameterDto } from '../../_models/accounts-payable/service-charge/ap-service-charge-editor-parameter-dto';
import { APServiceChargeEditorResultDto } from '../../_models/accounts-payable/service-charge/ap-service-charge-editor-result-dto';
import { APServiceChargeEditorSubcategoriesParameterDto } from '../../_models/accounts-payable/service-charge/ap-service-charge-editor-subcategories-parameter-dto';
import { APServiceChargeRawDto } from '../../_models/accounts-payable/service-charge/ap-service-charge-raw-dto';
import { APCreditApplicationResultDto } from '../../_models/accounts-payable/ap-credit-application/ap-credit-application-result-dto';
import { APCreditApplicationParameterDto } from '../../_models/accounts-payable/ap-credit-application/ap-credit-application-parameter-dto';

@Component({
    selector: 'app-ap-payment-details',
    templateUrl: './ap-payment-details.component.html',
    styleUrls: ['./ap-payment-details.component.scss']
})
export class APPaymentDetailsComponent implements OnInit {
    @Input() visible: boolean;
    @Input() apPaymentParam: APPaymentParameterDto;

    @Output() onAPPaymentClose = new EventEmitter<boolean>();

    contextCommunityId: number;

    apPaymentDetail: APPaymentResultDto;
    dateFormat: any;
    popUpTitle: string;
    defaultLabelLocation: string;
    historyButtonOptions: any;

    isInternalUser = false;
    loadingVisible: boolean;

    showVoidPayment = false;
    voidPaymentTitle: string;
    addVoidButtonOptions = '';

    showPaymentPostDateEditor = false;
    updatePaymentTitle: string;
    addUpdatePaymentButtonOptions = '';

    showAddServiceChargeEditor = false;
    addServiceChargeTitle: string;
    addAddChargeButtonOptions = '';
    serviceChargeParam: APServiceChargeEditorParameterDto;
    serviceChargeEditorData: APServiceChargeEditorResultDto;
    subCategories: GLSubcategorySelectorResultDto[] = [];

    showTransactionLog = false;
    transactionLogTitle: string;
    transactionLogs: Array<TransactionLogResultDto> = [];

    showCreditApplication = false;
    creditApplicationData: APCreditApplicationResultDto[] = [];
    transactionType = 'P';
    currencyFormat: any;
    creditApplicationTitle: string;

    iconCancel = IconConstants.CANCEL;
    iconEdit = IconConstants.EDIT;
    iconAdd = IconConstants.ADD;
    iconHistory = IconConstants.HISTORY;

    constructor(
        private readonly apPaymentService: APPaymentService,
        private readonly errorService: ServerErrorService,
        private readonly helperService: HelperService,
        private readonly apCreditApplicationService: APCreditApplicationsService,
        private readonly precisionService: PrecisionService,
        private readonly contextService: ContextManagerService,
        private readonly userService: UserService
    ) {
        this.isInternalUser = this.userService.isInternal();

        this.onTransactionLogClick = this.onTransactionLogClick.bind(this);
        this.onVoidClick = this.onVoidClick.bind(this);
        this.onUpdatePaymentPostDate = this.onUpdatePaymentPostDate.bind(this);
        this.onAddChargeClick = this.onAddChargeClick.bind(this);
        this.onTransactionLogClick = this.onTransactionLogClick.bind(this);
        this.currencyFormat = this.precisionService.getCurrencyFormat();
    }

    ngOnInit() {
        const context = this.contextService.getSelectedContext();
        if (Utils.notNullAndDefined(context)) {
            this.contextCommunityId = context.communityId;
        }

        // Subscribe to context
        this.contextService.getContext().subscribe((cContext: PropertyCommunityInfoResultDto) => {
            if (Utils.notNullAndDefined(cContext)) {
                this.contextCommunityId = cContext.communityId;
            }
        });

        this.historyButtonOptions = {
            icon: this.iconHistory,
            hint: 'Transaction History',
            onClick: this.onTransactionLogClick
        };

        this.configureButtonOptions();

        this.defaultLabelLocation = Constants.defaultFormLabelLocation;
        this.dateFormat = StringFormatConstants.dateFormat;

        if (Utils.notNullAndDefined(this.apPaymentParam)) {
            this.popUpTitle = 'AP Payment ' + this.apPaymentParam.paymentId;
            this.populatePaymentDetail();
        }
    }

    onHidden(): void {
        this.visible = false;
        this.onAPPaymentClose.emit(this.apPaymentParam.clearCache);
    }

    onTransactionLogClick(): void {
        this.showTransactionLog = true;
        this.transactionLogTitle = `Transaction Log of #${this.apPaymentParam.paymentId}`;
        this.getTransactionLogs();
    }

    getFileIconByExtension(file: any): string {
        let fileIcon = '';
        if (Utils.notNullAndDefined(file.fileName)) {
            const fileName = file.fileName;
            fileIcon = this.helperService.getFileIconByExtension(fileName);
        }
        return fileIcon;
    }

    downloadPaymentDocument(fileName: string): void {
        const param: APPaymentParameterDto = new APPaymentParameterDto();
        param.communityId = this.apPaymentParam.communityId;
        param.managementCompanyId = this.apPaymentParam.managementCompanyId;
        param.paymentId = this.apPaymentParam.paymentId;
        param.clearCache = this.apPaymentParam.clearCache;
        this.loadingVisible = true;
        this.apPaymentService.getAPPaymentDownloadDocument(param).subscribe(
            (file: Blob) => {
                const blob = new Blob([file], { type: file.type });
                this.helperService.downloadFileUsingBlob(blob, fileName);
                this.loadingVisible = false;
            },
            (error) => {
                this.loadingVisible = false;
                this.errorService.displayError(error);
            }
        );
    }

    onVoidClick(): void {
        this.showVoidPayment = true;
        this.voidPaymentTitle = 'Void The AP Payment';
    }

    onVoidPaymentClose(forceReload: boolean): void {
        this.showVoidPayment = false;

        if (Utils.isTrue(forceReload)) {
            this.apPaymentParam.clearCache = true;
            this.populatePaymentDetail();
        }
    }

    onUpdatePaymentPostDate(): void {
        this.showPaymentPostDateEditor = true;
        this.updatePaymentTitle = 'Update AP Payment Post Date';
    }

    onPaymentPostDateEditorClose(forceReload: boolean): void {
        this.showPaymentPostDateEditor = false;
        if (Utils.isTrue(forceReload)) {
            this.apPaymentParam.clearCache = true;
            this.populatePaymentDetail();
        }
    }

    onTransactionLogClose(): void {
        this.showTransactionLog = false;
    }

    onAddChargeClick(): void {
        this.addServiceChargeTitle = 'Add Service Charge';
        this.populateServiceChargeEditor();
    }

    getSubcategories(serviceCharge: APServiceChargeRawDto): void {
        if (Utils.notNullAndDefined(serviceCharge)) {
            this.populateServiceChargeSubcategories(serviceCharge);
        }
    }

    addAPServiceCharge(param: APServiceChargeRawDto): void {
        this.addServiceCharge(param);
    }

    closePopUp(): void {
        this.showAddServiceChargeEditor = false;
    }

    currencyFormatter(value: number): string {
        return formatNumber(value, this.currencyFormat);
    }

    closeCreditApplication(refresh: boolean): void {
        this.showCreditApplication = false;
        if (Utils.isTrue(refresh)) {
            this.apPaymentParam.clearCache = true;
            this.populatePaymentDetail();
        }
    }

    showAPCreditApplication(): void {
        const param: APCreditApplicationParameterDto = new APCreditApplicationParameterDto();
        param.communityId = this.apPaymentParam.communityId;
        param.managementCompanyId = this.apPaymentParam.managementCompanyId;
        param.creditApplicationId = null;
        param.sourceId = this.apPaymentParam.paymentId;
        param.sourceType = 'P';
        param.clearCache = true;

        this.loadingVisible = true;
        this.apCreditApplicationService.getAPCreditApplications(param).subscribe(
            (result: APCreditApplicationResultDto[]) => {
                this.creditApplicationData = result;
                this.creditApplicationTitle = `Payment Application for Payment Number ${this.apPaymentParam.paymentId}`;
                this.showCreditApplication = true;
                this.loadingVisible = false;
            },
            (error) => {
                this.loadingVisible = false;
                this.errorService.displayError(error);
            }
        );
    }

    onVendorNameClick(): void {
        if (Utils.notNullAndDefined(this.apPaymentDetail)) {
            const contextType = 'Community';
            const contextId = this.apPaymentParam.communityId;
            const vendorCode = this.apPaymentParam.vendorCode;
            const queryString = `contextType=${encodeURIComponent(
                contextType
            )}&contextId=${contextId}&vendorCode=${encodeURIComponent(vendorCode)}`;
            this.helperService.openDefaultVendorManagementPageInNewWindow(queryString);
        }
    }

    private populatePaymentDetail(): void {
        const param: APPaymentParameterDto = new APPaymentParameterDto();
        param.communityId = this.apPaymentParam.communityId;
        param.managementCompanyId = this.apPaymentParam.managementCompanyId;
        param.paymentId = this.apPaymentParam.paymentId;
        param.clearCache = this.apPaymentParam.clearCache;
        this.loadingVisible = true;
        this.apPaymentService.getAPPaymentDetail(param).subscribe(
            (result: APPaymentResultDto) => {
                this.apPaymentDetail = result;
                this.loadingVisible = false;
            },
            (error) => {
                this.loadingVisible = false;
                this.onHidden();
                this.errorService.displayError(error);
            }
        );
    }

    private getTransactionLogs(): void {
        const baseParam: TransactionLogParameterDto = new TransactionLogParameterDto();
        baseParam.transactionId = this.apPaymentParam.paymentId;

        this.loadingVisible = true;
        this.apPaymentService.getAPPayemntTransactionLog(baseParam).subscribe(
            (result: TransactionLogResultDto[]) => {
                this.transactionLogs = result;
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private populateServiceChargeEditor(): void {
        this.serviceChargeParam = new APServiceChargeEditorParameterDto();
        this.serviceChargeParam.apPaymentId = this.apPaymentParam.paymentId;
        this.serviceChargeParam.communityId = this.apPaymentParam.communityId;
        this.serviceChargeParam.managementCompanyId = this.apPaymentParam.managementCompanyId;

        this.apPaymentService.getServiceChargeEditor(this.serviceChargeParam).subscribe(
            (result: APServiceChargeEditorResultDto) => {
                this.serviceChargeEditorData = result;
                this.showAddServiceChargeEditor = true;
            },
            (error) => {
                this.errorService.displayError(error);
            }
        );
    }

    private populateServiceChargeSubcategories(serviceCharge: APServiceChargeRawDto): void {
        if (Utils.notNullAndDefined(this.serviceChargeParam)) {
            const param: APServiceChargeEditorSubcategoriesParameterDto =
                new APServiceChargeEditorSubcategoriesParameterDto();
            param.glAccountNumber = serviceCharge.glAccountNumber;
            param.communityId = this.serviceChargeParam.communityId;
            param.managementCompanyId = this.serviceChargeParam.managementCompanyId;
            param.clearCache = true;

            this.apPaymentService.getServiceChargeEditorSubcategories(param).subscribe(
                (result: GLSubcategorySelectorResultDto[]) => {
                    this.subCategories = result;
                    const selectedSubCategory = Utils.notNullAndDefined(serviceCharge.subCategoryCode)
                        ? this.subCategories.find((d) => Utils.equalsIgnoreCase(d.code, serviceCharge.subCategoryCode))
                        : this.subCategories[0];
                    if (Utils.notNullAndDefined(selectedSubCategory)) {
                        serviceCharge.subCategoryCode = selectedSubCategory.code;
                        setTimeout(() => {
                            this.serviceChargeEditorData.serviceCharge = serviceCharge;
                        });
                    }
                },
                (error) => {
                    this.errorService.displayError(error);
                }
            );
        }
    }

    private addServiceCharge(param: APServiceChargeRawDto): void {
        this.loadingVisible = true;
        this.apPaymentService.addServiceCharge(param).subscribe(
            () => {
                if (Utils.isTrue(param.payBill)) {
                    notify(
                        `Successfully added and paid including additional charge to Bill ${param.apPaymentId}`,
                        'success',
                        MessageInterval.successMsgInterval
                    );
                } else {
                    notify(
                        `Successfully added and paid service charge to Bill ${param.apPaymentId}`,
                        'success',
                        MessageInterval.successMsgInterval
                    );
                }
                this.showAddServiceChargeEditor = false;
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private configureButtonOptions(): void {
        this.addVoidButtonOptions = JSON.stringify({
            beforeIcons: [IconConstants.CLOSE],
            label: 'Void'
        });

        this.addUpdatePaymentButtonOptions = JSON.stringify({
            beforeIcons: [IconConstants.EDIT_NEW],
            label: 'Update Payment Post Date'
        });

        this.addAddChargeButtonOptions = JSON.stringify({
            beforeIcons: [IconConstants.ADD],
            label: 'Add Service Charge'
        });
    }
}
